import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AuthLayout } from '@/features/auth/auth-layout'
import { UserAuthForm } from '@/features/auth/sign-in/user-auth-form'

type SignInProps = {
  onSignIn: (payload: { email: string; password: string }) => Promise<void>
  isSigningIn: boolean
}

export function SignIn({ onSignIn, isSigningIn }: SignInProps) {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const [serverError, setServerError] = useState<string | null>(null)

  const signUpPath = useMemo(() => {
    const redirect = searchParams.get('redirect')
    if (redirect) {
      return `/signup?redirect=${encodeURIComponent(redirect)}`
    }
    return '/signup'
  }, [searchParams])

  const onSignInSubmit = useCallback(
    async (payload: { email: string; password: string }) => {
      setServerError(null)
      try {
        await onSignIn(payload)
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 401) {
          setServerError('errors.invalidEmailOrPassword')
        } else {
          setServerError('errors.unableToSignIn')
        }
      }
    },
    [onSignIn]
  )

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg tracking-tight">
            {t('actions.signIn')}
          </CardTitle>
          <CardDescription>
            {t('description')}
            <>
              <br />
              {t('signUpPrompt')}{' '}
              <Link
                to={signUpPath}
                className="underline underline-offset-4 hover:text-primary"
              >
                {t('signUpLink')}
              </Link>
            </>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {serverError && (
            <Alert variant="destructive">
              <AlertDescription>
                {serverError ? t(serverError) : null}
              </AlertDescription>
            </Alert>
          )}
          <UserAuthForm onSignIn={onSignInSubmit} isSigningIn={isSigningIn} />
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
